import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { cancelOrder, getOrderById } from '../../services/orderApi.js';
import { getWaiters } from '../../services/waiterApi.js';
import ConfirmDialog from '../common/ConfirmDialog.js';
import SearchField from '../common/SearchField.js';
import PrintDialog from '../common/PrintDialog.js';
import OrderPrintContent from '../common/OrderPrintContent.js';
import Button from '../common/Button.js';
import Pagination from '../common/Pagination.js';
import { useOrderStore } from '../../store/branchstore/branchOrdersStore.js';
import useAppStore from '../../store/useAppStore.js';

import { convertToPKT } from '../../utils/timeZone.js'; 

const Orders = ({ onEditOrder }) => {
  const { orders, loading, loadingMore, error, hasMoreOrders, initializeOrders, loadNextOrdersBatch, updateOrderStatus } = useOrderStore((state) => ({
    orders: state.orders,
    loading: state.loading,
    loadingMore: state.loadingMore,
    error: state.error,
    hasMoreOrders: state.hasMoreOrders,
    initializeOrders: state.initializeOrders,
    loadNextOrdersBatch: state.loadNextOrdersBatch,
    updateOrderStatus: state.updateOrderStatus,
  }));
  const [waiters, setWaiters] = useState([]);
  const [waitersError, setWaitersError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOrderType, setFilterOrderType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, orderId: null, action: null });
  const [currentPrintOrder, setCurrentPrintOrder] = useState(null);
  const [isPrintReady, setIsPrintReady] = useState(false);
  const [printMode, setPrintMode] = useState('receipt');
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt-${new Date().getTime()}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 0;
      }
    `,
    onAfterPrint: () => {
      setCurrentPrintOrder(null);
      setIsPrintReady(false);
      setIsPrintDialogOpen(false);
    }
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([initializeOrders(), getWaiters().then((response) => {
          setWaiters(response.waiters || []);
          setWaitersError('');
        })]);
      } catch (err) {
        console.error('Error loading orders or waiters:', err);
        if (!error) {
          setWaitersError('Failed to load waiters');
        }
      }
    };

    loadData();
  }, []); // Empty dependency array - run only once on mount

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterOrderType, filterStatus]);

  const getWaiterName = (waiterId) => {
    if (!waiterId) return 'N/A';
    const waiter = waiters.find(w => w.id === waiterId);
    return waiter ? waiter.name : `Waiter #${waiterId}`;
  };

  const getOrderTypeDisplay = (orderType, tableName, waiterName) => {
    switch (orderType) {
      case 'walkin':
        return 'Walk-in';
      case 'table':
        return `Table: ${tableName || 'N/A'}`;
      case 'waiter':
        return `Waiter: ${waiterName}`;
      default:
        return orderType;
    }
  };

  const filteredOrders = useMemo(() => orders.filter(order => {
    const matchesSearch = !searchQuery ||
      (order.order_number && order.order_number.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
      order.id.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      getWaiterName(order.waiter_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.table_name && order.table_name.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesType = !filterOrderType || order.order_type === filterOrderType;
    const matchesStatus = !filterStatus || order.status === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  }), [orders, searchQuery, filterOrderType, filterStatus, waiters]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredOrders.slice(startIndex, startIndex + pageSize);
  }, [filteredOrders, currentPage, pageSize]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredOrders.length / pageSize)), [filteredOrders.length, pageSize]);

  const handleFetchMore = useCallback(async () => {
    if (!hasMoreOrders || loadingMore) return;
    await loadNextOrdersBatch();
    setCurrentPage((prevPage) => prevPage + 1);
  }, [hasMoreOrders, loadingMore, loadNextOrdersBatch]);

  const handlePrintOrder = (order) => {
    setCurrentPrintOrder(order);
    setIsPrintDialogOpen(true);
    setPrintMode('receipt'); // Reset to receipt as default
  };

  const handlePrintModeSelected = (mode) => {
    if (mode === 'none') {
      // No print - just close the dialog
      setCurrentPrintOrder(null);
      setIsPrintDialogOpen(false);
      return;
    }
    // Set print mode and close dialog - this will trigger the print flow
    setPrintMode(mode);
    setIsPrintReady(false);
    setIsPrintDialogOpen(false); // Close dialog to allow OrderPrintContent to render
  };

  useEffect(() => {
    if (currentPrintOrder && isPrintReady && isPrintDialogOpen === false) {
      const timer = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentPrintOrder, isPrintReady, isPrintDialogOpen, handlePrint]);

  const handleCancelOrder = (orderId) => {
    setConfirmDialog({ isOpen: true, orderId, action: 'cancel' });
  };

  const handleEditOrder = (order) => {
    // Load full order details and switch to Sales for editing
    try {
      const { loadOrderForEditing } = useAppStore.getState();
      
      // Fetch full order details
      getOrderById(order.id).then((response) => {
        const fullOrder = response.order;
        // Load the order for editing
        loadOrderForEditing(fullOrder);
        // Navigate to Sales
        if (onEditOrder) {
          onEditOrder();
        }
      }).catch((error) => {
        console.error('Error loading order:', error);
      });
    } catch (error) {
      console.error('Error in edit order:', error);
    }
  };

  const handleConfirmAction = async () => {
    const { orderId, action } = confirmDialog;

    if (action === 'cancel') {
      try {
        const result = await cancelOrder(orderId);
        // Update the order status in the store instead of refreshing all orders
        updateOrderStatus(orderId, { status: 'cancelled' });
      } catch (err) {
        console.error('Error cancelling order:', err);
      }
    }

    setConfirmDialog({ isOpen: false, orderId: null, action: null });
  };
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'confirmed':
        return 'bg-blue-100 text-blue-700';
      case 'ready':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const formatAmount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Orders</h1>
          <p className="text-gray-600 mt-2">View and manage all orders across walk-in, tables, and waiters</p>
        </div>
      </div>

      {(error || waitersError) && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
          {error || waitersError}
        </div>
      )}

      {/* Filter Section */}
      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by order ID, waiter, or table..."
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
            <select
              value={filterOrderType}
              onChange={(e) => setFilterOrderType(e.target.value)}
              className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              <option value="">All Types</option>
              <option value="walkin">Walk-in</option>
              <option value="table">Table</option>
              <option value="waiter">Waiter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => {
                setSearchQuery('');
                setFilterOrderType('');
                setFilterStatus('');
              }}
              variant="secondary"
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm h-[500px">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">All Orders</h2>
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {filteredOrders.length} orders
            </span>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchQuery || filterOrderType || filterStatus ? 'No orders match your filters' : 'No orders found'}
          </div>
        ) : (
          <>
            <div className="overflow-auto h-[400px]">
              <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-700">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Cashier</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Created</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedOrders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-gray-900">ORD-{order.order_number || order.id}</span>
                      {(order.token_number || order.token?.token_no) && (
                        <div className="text-xs text-gray-500">Token: {order.token_number || order.token?.token_no}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-600">
                        {getOrderTypeDisplay(order.order_type, order.table_name, getWaiterName(order.waiter_id))}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="font-semibold text-gray-900">{formatAmount(order.total)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-600">{order.cashier_name || order.user_name || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.created_at && new Date(order.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleEditOrder(order)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg border border-blue-300 hover:bg-blue-50 transition text-blue-600 hover:text-blue-900"
                          title="Edit Order"
                        >
                          <span className="text-sm">✎</span>
                        </button>
                        <button
                          onClick={() => handlePrintOrder(order)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-300 hover:bg-gray-50 transition text-gray-600 hover:text-gray-900"
                          title="Print Receipt"
                        >
                          <span className="text-sm">🖨️</span>
                        </button>
                        {order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            className="flex items-center justify-center w-8 h-8 rounded-lg border border-red-300 hover:bg-red-50 transition text-red-600 hover:text-red-900"
                            title="Cancel Order"
                          >
                            <span className="text-sm">✕</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loadingMore && (
            <div className="px-6 py-3 text-sm text-gray-600">Loading more orders...</div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={filteredOrders.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            hasMore={hasMoreOrders}
            onFetchMore={handleFetchMore}
            className="bg-white"
          />
        </>
        )}
      </div>

      {/* Print Dialog */}
      <PrintDialog
        isOpen={isPrintDialogOpen}
        onClose={() => {
          setIsPrintDialogOpen(false);
          // Don't clear the order here - let the print process handle it
        }}
        onSelectPrintMode={handlePrintModeSelected}
      />

      {/* Print Content */}
      {currentPrintOrder && !isPrintDialogOpen && (
        <div className="hidden">
          <OrderPrintContent
            ref={printRef}
            order={currentPrintOrder}
            printMode={printMode}
            onReady={() => setIsPrintReady(true)}
          />
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.action === 'cancel' ? 'Cancel Order' : 'Confirm Action'}
        message={
          confirmDialog.action === 'cancel'
            ? 'Are you sure you want to cancel this order? This action cannot be undone.'
            : 'Are you sure you want to perform this action?'
        }
        onConfirm={handleConfirmAction}
        onClose={() => setConfirmDialog({ isOpen: false, orderId: null, action: null })}
      />
    </div>
  );
};

export default Orders;